/**
 * WelcomeWizard.js - V1 Commercial Readiness Remediation
 * 
 * GUIDED ONBOARDING EXPERIENCE
 * - Role-based introduction
 * - Feature discovery
 * - Getting started checklist
 * 
 * Solves: "Where do I start?" confusion
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Gauge,
  Target,
  Briefcase,
  Brain,
  FolderKanban,
  Users,
  Shield,
  Building2,
  CheckCircle,
  ArrowRight,
  ChevronRight,
  Sparkles,
  FileCheck,
  BarChart3,
  Calendar,
  AlertTriangle,
  TrendingUp,
  Clock,
  X,
  Check,
  Play
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api/client';

// =============================================================================
// ROLE CONFIGURATIONS
// =============================================================================
const ROLE_CONFIGS = {
  executive: {
    title: "Executive / Sponsor",
    description: "Strategic oversight and decision-making",
    color: "blue",
    icon: Gauge,
    landingPath: "/executive",
    keyFeatures: [
      { path: "/executive", label: "Executive Hub", description: "Your command center for portfolio health", icon: Gauge },
      { path: "/governance/decision-ledger", label: "Decision Ledger", description: "Audit trail of all governance decisions", icon: FileCheck },
      { path: "/governance/benefits", label: "Benefits Dashboard", description: "Track value realization", icon: TrendingUp },
    ],
    quickActions: [
      { label: "View Portfolio Health", path: "/executive" },
      { label: "Check Pending Decisions", path: "/executive?tab=actions" },
      { label: "Review Benefits Status", path: "/governance/benefits" },
    ]
  },
  pmo: {
    title: "PMO Director / Portfolio Manager",
    description: "Portfolio governance and project oversight",
    color: "purple",
    icon: Target,
    landingPath: "/dashboard/pmo",
    keyFeatures: [
      { path: "/dashboard/pmo", label: "PMO Center", description: "Project health and status overview", icon: Target },
      { path: "/demands", label: "Demand Board", description: "Manage intake and prioritization", icon: FolderKanban },
      { path: "/portfolio/hierarchy", label: "Portfolio Hierarchy", description: "Structure and relationships", icon: Building2 },
      { path: "/executive", label: "Executive Hub", description: "Org-wide health scores", icon: Gauge },
    ],
    quickActions: [
      { label: "Review Project Health", path: "/dashboard/pmo" },
      { label: "Check Demand Pipeline", path: "/demands" },
      { label: "Manage Portfolio Structure", path: "/portfolio/hierarchy" },
    ]
  },
  pm: {
    title: "Project Manager",
    description: "Project execution and team coordination",
    color: "green",
    icon: FolderKanban,
    landingPath: "/dashboard/project-manager",
    keyFeatures: [
      { path: "/dashboard/project-manager", label: "PM Dashboard", description: "Your projects at a glance", icon: FolderKanban },
      { path: "/ai/pm-assistant", label: "PM Assistant", description: "AI-powered daily brief and recommendations", icon: Brain },
      { path: "/work-items", label: "Work Items", description: "Tasks, features, and epics", icon: CheckCircle },
      { path: "/issues", label: "Issues & Risks", description: "Track and manage RAID items", icon: AlertTriangle },
    ],
    quickActions: [
      { label: "See Today's Brief", path: "/ai/pm-assistant" },
      { label: "Update Project Status", path: "/dashboard/project-manager" },
      { label: "Manage Work Items", path: "/work-items" },
    ]
  },
  viewer: {
    title: "Team Member / Viewer",
    description: "View-only access to dashboards and reports",
    color: "slate",
    icon: Users,
    landingPath: "/analytics",
    keyFeatures: [
      { path: "/analytics", label: "Dashboards", description: "Portfolio and project insights", icon: BarChart3 },
      { path: "/roadmap", label: "Roadmap", description: "Strategic timeline view", icon: Calendar },
      { path: "/issues", label: "Issues & Risks", description: "Current risks and issues", icon: AlertTriangle },
    ],
    quickActions: [
      { label: "View Dashboards", path: "/analytics" },
      { label: "Check Roadmap", path: "/roadmap" },
    ]
  }
};

// =============================================================================
// GETTING STARTED CHECKLIST
// =============================================================================
const GETTING_STARTED_ITEMS = {
  executive: [
    { id: 'view_hub', label: 'View Executive Hub', path: '/executive' },
    { id: 'check_scores', label: 'Review Intelligence Scores', path: '/executive?tab=intelligence' },
    { id: 'see_actions', label: 'Check Required Actions', path: '/executive?tab=actions' },
    { id: 'review_portfolio', label: 'Explore Portfolio', path: '/executive?tab=portfolio' },
  ],
  pmo: [
    { id: 'view_pmo', label: 'View PMO Center', path: '/dashboard/pmo' },
    { id: 'check_demands', label: 'Review Demand Pipeline', path: '/demands' },
    { id: 'see_hierarchy', label: 'Explore Portfolio Hierarchy', path: '/portfolio/hierarchy' },
    { id: 'check_benefits', label: 'Review Benefits Status', path: '/governance/benefits' },
  ],
  pm: [
    { id: 'view_dashboard', label: 'View Your Dashboard', path: '/dashboard/project-manager' },
    { id: 'check_brief', label: 'Review AI Daily Brief', path: '/ai/pm-assistant' },
    { id: 'see_work', label: 'Check Work Items', path: '/work-items' },
    { id: 'review_risks', label: 'Review Risks & Issues', path: '/issues' },
  ],
  viewer: [
    { id: 'view_dashboards', label: 'Explore Dashboards', path: '/analytics' },
    { id: 'check_roadmap', label: 'View Roadmap', path: '/roadmap' },
    { id: 'see_issues', label: 'Review Issues & Risks', path: '/issues' },
  ]
};

// =============================================================================
// FEATURE CARD COMPONENT
// =============================================================================
const FeatureCard = ({ feature, onClick }) => {
  const Icon = feature.icon;
  
  return (
    <div 
      className="p-4 bg-white border rounded-lg hover:shadow-md cursor-pointer transition-all group"
      onClick={() => onClick(feature.path)}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
          <Icon className="w-5 h-5 text-blue-600" />
        </div>
        <div className="flex-1">
          <p className="font-semibold text-gray-900">{feature.label}</p>
          <p className="text-sm text-gray-500 mt-0.5">{feature.description}</p>
        </div>
        <ChevronRight className="w-5 h-5 text-gray-400 group-hover:translate-x-1 transition-transform" />
      </div>
    </div>
  );
};

// =============================================================================
// CHECKLIST ITEM COMPONENT
// =============================================================================
const ChecklistItem = ({ item, completed, onComplete, onNavigate }) => {
  return (
    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
      <button
        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
          completed 
            ? 'bg-green-500 border-green-500 text-white' 
            : 'border-gray-300 hover:border-green-400'
        }`}
        onClick={() => onComplete(item.id)}
      >
        {completed && <Check className="w-4 h-4" />}
      </button>
      <span className={`flex-1 ${completed ? 'text-gray-400 line-through' : 'text-gray-700'}`}>
        {item.label}
      </span>
      <Button 
        variant="ghost" 
        size="sm"
        onClick={() => onNavigate(item.path)}
      >
        <Play className="w-4 h-4 mr-1" /> Go
      </Button>
    </div>
  );
};

// =============================================================================
// MAIN WELCOME WIZARD COMPONENT
// V1 COMPLETION: Simplified - auto-detects role, skips role selection step
// =============================================================================
const WelcomeWizard = ({ onComplete }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // V1: Start at step 2 (skip role selection since we auto-detect)
  const [step, setStep] = useState(2);
  const [selectedRole, setSelectedRole] = useState(null);
  const [completedItems, setCompletedItems] = useState([]);
  const [dismissed, setDismissed] = useState(false);

  // Auto-detect role from user profile - no manual selection needed
  useEffect(() => {
    if (user?.role) {
      const roleMapping = {
        'executive': 'executive',
        'pmo_power': 'pmo',
        'pmo_standard': 'pmo',
        'pmo_analyst': 'pmo',
        'program_admin': 'pmo',
        'pm': 'pm',
        'delivery_lead': 'pm',
        'business_analyst': 'pm',
        'viewer': 'viewer',
        'admin': 'pmo',
        'sysadmin': 'pmo',
      };
      const mappedRole = roleMapping[user.role] || 'viewer';
      setSelectedRole(mappedRole);
    }
  }, [user]);

  // Load saved checklist progress (for mini-widget state)
  useEffect(() => {
    const saved = localStorage.getItem(`scopeops_onboarding_${user?.id}`);
    if (saved) {
      try {
        const data = JSON.parse(saved);
        setCompletedItems(data.completedItems || []);
        if (data.dismissed) {
          setDismissed(true);
        }
      } catch (e) {
        console.warn('Failed to load onboarding progress');
      }
    }
  }, [user?.id]);

  // Save progress locally (for checklist state)
  const saveProgress = (items, isDismissed = false) => {
    localStorage.setItem(`scopeops_onboarding_${user?.id}`, JSON.stringify({
      completedItems: items,
      dismissed: isDismissed,
      lastUpdated: new Date().toISOString()
    }));
  };

  const handleComplete = (itemId) => {
    const newCompleted = completedItems.includes(itemId)
      ? completedItems.filter(id => id !== itemId)
      : [...completedItems, itemId];
    setCompletedItems(newCompleted);
    saveProgress(newCompleted);
  };

  const handleNavigate = (path) => {
    navigate(path);
  };

  // V1 COMPLETION: Use backend persistence via onComplete callback
  const handleDismiss = () => {
    setDismissed(true);
    saveProgress(completedItems, true);
    // Persist to backend so wizard never shows again
    if (onComplete) {
      onComplete();
    }
  };

  const handleReset = () => {
    setDismissed(false);
    setCompletedItems([]);
    setStep(1);
    saveProgress([], false);
  };

  const roleConfig = selectedRole ? ROLE_CONFIGS[selectedRole] : null;
  const checklistItems = selectedRole ? GETTING_STARTED_ITEMS[selectedRole] : [];
  const progress = checklistItems.length > 0 
    ? Math.round((completedItems.length / checklistItems.length) * 100) 
    : 0;

  // If dismissed, show mini progress widget
  if (dismissed) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <div className="bg-white rounded-lg shadow-lg border p-4 max-w-xs">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Getting Started</span>
            <Button variant="ghost" size="sm" onClick={handleReset}>
              <Sparkles className="w-4 h-4 mr-1" /> Restart
            </Button>
          </div>
          <Progress value={progress} className="h-2" />
          <p className="text-xs text-gray-500 mt-1">{progress}% complete</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="welcome-wizard">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-auto">
        
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Welcome to ScopeOps</h1>
                <p className="text-blue-100">
                  {step === 1 && "Let's get you started"}
                  {step === 2 && "Discover key features"}
                  {step === 3 && "Your getting started checklist"}
                </p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-white hover:bg-white/20"
              onClick={handleDismiss}
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
          
          {/* Progress Steps */}
          <div className="flex items-center gap-2 mt-6">
            {[1, 2, 3].map((s) => (
              <div 
                key={s}
                className={`flex-1 h-1 rounded-full ${
                  s <= step ? 'bg-white' : 'bg-white/30'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          
          {/* Step 1: Role Selection */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  What's your primary role?
                </h2>
                <p className="text-gray-500">
                  We'll customize your experience based on your role
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {Object.entries(ROLE_CONFIGS).map(([key, config]) => {
                  const Icon = config.icon;
                  return (
                    <div
                      key={key}
                      className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                        selectedRole === key
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => setSelectedRole(key)}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          selectedRole === key ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-500'
                        }`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{config.title}</p>
                          <p className="text-xs text-gray-500">{config.description}</p>
                        </div>
                      </div>
                      {selectedRole === key && (
                        <CheckCircle className="w-5 h-5 text-blue-500 absolute top-3 right-3" />
                      )}
                    </div>
                  );
                })}
              </div>

              <Button 
                className="w-full" 
                size="lg"
                disabled={!selectedRole}
                onClick={() => setStep(2)}
              >
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {/* Step 2: Key Features */}
          {step === 2 && roleConfig && (
            <div className="space-y-6">
              <div className="text-center">
                <Badge className="mb-3 bg-blue-100 text-blue-700">
                  {roleConfig.title}
                </Badge>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  Key Features for You
                </h2>
                <p className="text-gray-500">
                  Here are the most important features for your role
                </p>
              </div>

              <div className="space-y-3">
                {roleConfig.keyFeatures.map((feature, idx) => (
                  <FeatureCard 
                    key={idx} 
                    feature={feature} 
                    onClick={handleNavigate}
                  />
                ))}
              </div>

              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setStep(1)}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button 
                  className="flex-1"
                  onClick={() => setStep(3)}
                >
                  Continue <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Getting Started Checklist */}
          {step === 3 && roleConfig && (
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  Getting Started Checklist
                </h2>
                <p className="text-gray-500">
                  Complete these items to get the most out of ScopeOps
                </p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-gray-500">Progress</span>
                  <span className="text-sm font-medium text-gray-900">{progress}%</span>
                </div>
                <Progress value={progress} className="h-2" />
              </div>

              <div className="space-y-2">
                {checklistItems.map((item) => (
                  <ChecklistItem
                    key={item.id}
                    item={item}
                    completed={completedItems.includes(item.id)}
                    onComplete={handleComplete}
                    onNavigate={handleNavigate}
                  />
                ))}
              </div>

              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setStep(2)}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button 
                  className="flex-1"
                  onClick={() => navigate(roleConfig.landingPath)}
                >
                  Go to {roleConfig.title.split('/')[0].trim()} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>

              <div className="text-center">
                <button 
                  className="text-sm text-gray-500 hover:text-gray-700"
                  onClick={handleDismiss}
                >
                  Skip for now, I'll explore on my own
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default WelcomeWizard;
